import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useState } from 'react';
import {
    AckUnitEmergencyRequest,
    ApiResult,
    ApiResultAckUnitEmergencyResponse,
    ApiResultCreateUnitEmergencyResponse,
    ApiResultEndUnitEmergencyResponse,
    CreateUnitEmergencyRequest,
    UnitEmergencyAckDto,
    UnitEmergencyDto,
    UnitResource,
} from '@mark43/cad-api';
import { useIntegratedUnitEmergencies } from './use-integrated-unit-emergencies';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { useToast } from '@cad-arc';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { AxiosError, AxiosResponse } from 'axios-cad-nx';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';

export type UnitEmergencyContextType = {
    addAcknowledgedUnitEmergency: (ackUnitEmergencyDto: UnitEmergencyAckDto) => void;
    ackedUnitEmergencies: UnitEmergencyAckDto[];
    unAckedUnitEmergencies: UnitEmergencyDto[];
    activeUnitEmergency: UnitEmergencyDto | null;
    filteredUnAckedUnitEmergencies: UnitEmergencyDto[];
    cancelEmergencyModalOpen: boolean;
    setCancelEmergencyModalOpen: Dispatch<SetStateAction<boolean>>;
    timer: boolean;
    setTimer: Dispatch<SetStateAction<boolean>>;
    endTime: Date | null;
    setEndTime: Dispatch<SetStateAction<Date | null>>;
    secondsRemaining: number;
    setSecondsRemaining: Dispatch<SetStateAction<number>>;
    createUnitEmergencyMutation: UseMutationResult<
        AxiosResponse<ApiResultCreateUnitEmergencyResponse, any>,
        AxiosError<ApiResult, any>,
        CreateUnitEmergencyRequest,
        unknown
    >;
    endUnitEmergencyMutation: UseMutationResult<
        AxiosResponse<ApiResultEndUnitEmergencyResponse, any>,
        AxiosError<ApiResult, any>,
        number,
        unknown
    >;
    createUnitEmergency: (
        unitEmergencyDto: CreateUnitEmergencyRequest
    ) => Promise<AxiosResponse<ApiResultCreateUnitEmergencyResponse, any>> | undefined;
    endUnitEmergency: (
        unitId: number
    ) => Promise<AxiosResponse<ApiResultEndUnitEmergencyResponse, any>> | undefined;
    acknowledgeUnitEmergency: (
        ackUnitEmergencyDto: AckUnitEmergencyRequest
    ) => Promise<AxiosResponse<ApiResultAckUnitEmergencyResponse, any>> | undefined;
};

export const UnitEmergencyContext = createContext<UnitEmergencyContextType | undefined>(undefined);

export function UnitEmergencyProvider({ children }: { children: ReactNode }) {
    const apiClient = useApiClient();
    const toast = useToast();
    const modalControls = useFullscreenModalControls();
    const { unitEmergencyEnabled } = useEnabledFeatures();
    const {
        unAckedUnitEmergencies,
        ackedUnitEmergencies,
        filteredUnAckedUnitEmergencies,
        addAcknowledgedUnitEmergency,
        activeUnitEmergency,
    } = useIntegratedUnitEmergencies({ enabled: unitEmergencyEnabled });
    const { unitEmergencyActivationCountdownSeconds } = useBootstrapState();
    const [secondsRemaining, setSecondsRemaining] = useState<number>(
        unitEmergencyActivationCountdownSeconds
    );
    const [timer, setTimer] = useState<boolean>(false);
    const [endTime, setEndTime] = useState<Date | null>(null);
    const [cancelEmergencyModalOpen, setCancelEmergencyModalOpen] = useState<boolean>(false);

    const { postMessage } = useBroadcast('acknowledge-unit-emergency', (message) => {
        addAcknowledgedUnitEmergency(message.payload);
    });

    const createUnitEmergencyMutation = useMutation({
        mutationFn: (unitEmergencyDto: CreateUnitEmergencyRequest) => {
            return apiClient.request<UnitResource.CreateUnitEmergency>({
                method: 'POST',
                url: `unit/unit_emergency/`,
                data: unitEmergencyDto,
            });
        },
        onError: createApiErrorHandler(() => {
            toast({
                status: 'error',
                description: errorOccurredWhile('creating unit emergency'),
            });
        }),
    });

    const createUnitEmergency = useCallback(
        (unitEmergencyDto: CreateUnitEmergencyRequest) => {
            if (createUnitEmergencyMutation.isLoading) {
                return;
            }
            return createUnitEmergencyMutation.mutateAsync(unitEmergencyDto);
        },
        [createUnitEmergencyMutation]
    );

    const endUnitEmergencyMutation = useMutation(
        (unitId: number) => {
            return apiClient.request<UnitResource.EndUnitEmergency>({
                method: 'POST',
                url: `unit/unit_emergency/end/`,
                data: { unitId },
            });
        },
        {
            onSuccess() {
                modalControls.close();
            },
            onError: createApiErrorHandler(() => {
                toast({
                    status: 'error',

                    description: errorOccurredWhile('ending emergency'),
                });
            }),
        }
    );

    const endUnitEmergency = useCallback(
        (unitId: number) => {
            if (endUnitEmergencyMutation.isLoading) {
                return;
            }

            return endUnitEmergencyMutation.mutateAsync(unitId);
        },
        [endUnitEmergencyMutation]
    );

    const acknowledgeUnitEmergencyMutation = useMutation(
        (ackUnitEmergencyDto: AckUnitEmergencyRequest) => {
            return apiClient.request<UnitResource.AckUnitEmergency>({
                method: 'POST',
                url: `unit/unit_emergency/ack`,
                data: ackUnitEmergencyDto,
            });
        },
        {
            onSuccess(data) {
                const ackData = data.data.data?.unitEmergencyAckDto;
                if (ackData) {
                    addAcknowledgedUnitEmergency(ackData);
                    postMessage({ type: 'ACKNOWLEDGE_UNIT_EMERGENCY', payload: ackData });
                }
            },
            onError: createApiErrorHandler(() => {
                toast({
                    status: 'error',
                    description: errorOccurredWhile('acknowledging unit emergency'),
                });
            }),
        }
    );

    const acknowledgeUnitEmergency = useCallback(
        (ackUnitEmergencyDto: AckUnitEmergencyRequest) => {
            if (acknowledgeUnitEmergencyMutation.isLoading) {
                return;
            }
            return acknowledgeUnitEmergencyMutation.mutateAsync(ackUnitEmergencyDto);
        },
        [acknowledgeUnitEmergencyMutation]
    );

    return (
        <UnitEmergencyContext.Provider
            value={{
                addAcknowledgedUnitEmergency,
                ackedUnitEmergencies,
                unAckedUnitEmergencies,
                filteredUnAckedUnitEmergencies,
                cancelEmergencyModalOpen,
                setCancelEmergencyModalOpen,
                secondsRemaining,
                setSecondsRemaining,
                createUnitEmergency,
                createUnitEmergencyMutation,
                endUnitEmergency,
                endUnitEmergencyMutation,
                acknowledgeUnitEmergency,
                activeUnitEmergency,
                timer,
                setTimer,
                endTime,
                setEndTime,
            }}
        >
            {children}
        </UnitEmergencyContext.Provider>
    );
}
